<template>
  <div class="p-m-3 sp-page">
    <MockBanner />
    <div class="sp-row">
      <h2 style="margin: 0;">Projects report</h2>
      <span class="sp-spacer"></span>
      <div class="p-d-flex p-ai-center" style="gap: 8px;">
        <InputSwitch id="spRepLive" v-model="liveOnly" />
        <label for="spRepLive" style="margin: 0;">Live only</label>
      </div>
      <div class="p-d-flex p-ai-center" style="gap: 8px;">
        <InputSwitch id="spRepGroup" v-model="groupBySite" />
        <label for="spRepGroup" style="margin: 0;">Group by site</label>
      </div>
      <div class="p-d-flex p-ai-center" style="gap: 8px;">
        <label style="margin: 0;">Value while tendered:</label>
        <SelectButton v-model="rule" :options="ruleOptions" optionLabel="label" optionValue="value" />
      </div>
    </div>
    <p class="sp-sub">
      One row per project and product type: the job counted once. While it is tendered, the value is the {{ rule }} of the live quotes
      (an admin setting, recommended: average). Once won, it is the won value. Switch the setting above to see the effect.
    </p>

    <div class="sp-row p-mb-3">
      <div class="sp-kpi">
        <div class="label">Quote bank, project basis</div><div class="value">{{ money(totalCounted) }}</div>
        <div class="hint">{{ rows.length }} project / product type lines</div>
      </div>
      <div class="sp-kpi">
        <div class="label">Quote bank today (every live quote added up)</div><div class="value sp-muted">{{ money(totalLiveSum) }}</div>
      </div>
      <div class="sp-kpi">
        <div class="label">Counted more than once today</div><div class="value" style="color: #b71c1c;">{{ money(totalLiveSum - totalCounted) }}</div>
        <div class="hint">{{ pct }}% of today's figure here, because these sample projects were picked as tenders. On all live TEST enquiries it was about 16% (analysis of 09/09).</div>
      </div>
    </div>

    <div class="p-card p-p-3">
      <DataTable
        :value="rows"
        :autoLayout="true"
        class="p-datatable-sm p-datatable-striped p-datatable-gridlines"
        :rowHover="true"
        :rowGroupMode="groupBySite ? 'subheader' : null"
        groupRowsBy="siteName"
        :sortField="groupBySite ? 'siteName' : null"
        :sortOrder="1"
      >
        <template #groupheader="s"><strong>{{ s.data.siteName }}</strong></template>
        <Column field="siteName" header="Site" :sortable="!groupBySite" />
        <Column field="projectName" header="Project" :sortable="!groupBySite">
          <template #body="s"><span class="sp-link" @click="$router.push('/sitesProjects/projects/' + s.data.projectId)">{{ s.data.projectName }}</span></template>
        </Column>
        <Column field="town" header="Town" :sortable="!groupBySite" />
        <Column field="productType" header="Product Type" :sortable="!groupBySite" />
        <Column field="branches" header="Branches" :sortable="!groupBySite" />
        <Column field="nCustomers" header="Customers quoting" :sortable="!groupBySite" bodyStyle="text-align: right;" />
        <Column field="low" header="Low £" :sortable="!groupBySite"><template #body="s"><div class="sp-money sp-muted">{{ s.data.nQuotes > 1 ? money(s.data.low) : '' }}</div></template></Column>
        <Column field="avg" header="Average £" :sortable="!groupBySite"><template #body="s"><div class="sp-money sp-muted">{{ s.data.nQuotes > 1 ? money(s.data.avg) : '' }}</div></template></Column>
        <Column field="high" header="High £" :sortable="!groupBySite"><template #body="s"><div class="sp-money sp-muted">{{ s.data.nQuotes > 1 ? money(s.data.high) : '' }}</div></template></Column>
        <Column field="countedValue" header="Counted £" :sortable="!groupBySite">
          <template #body="s"><div class="sp-money"><strong>{{ money(s.data.countedValue) }}</strong><span v-if="s.data.nQuotes > 1" class="sp-n">({{ s.data.nQuotes }})</span></div></template>
        </Column>
        <Column field="basis" header="Basis" :sortable="!groupBySite" />
        <Column field="liveSum" header="Live quotes added up £" :sortable="!groupBySite"><template #body="s"><div class="sp-money sp-muted">{{ money(s.data.liveSum) }}</div></template></Column>
        <Column field="overCounted" header="Over-counted £" :sortable="!groupBySite"><template #body="s"><div class="sp-money" :style="s.data.overCounted > 0 ? 'color:#b71c1c' : ''">{{ s.data.overCounted ? money(s.data.overCounted) : '' }}</div></template></Column>
        <template #footer>
          <div class="sp-row">
            <span class="sp-spacer"></span>
            <span>Counted: <strong>{{ money(totalCounted) }}</strong></span>
            <span>Live quotes added up: <strong>{{ money(totalLiveSum) }}</strong></span>
          </div>
        </template>
      </DataTable>
    </div>
  </div>
</template>

<script>
import MockBanner from './MockBanner.vue'
import { projects, siteById, productLines, settings, money } from './mockData'

export default {
  name: 'SpProjectsReport',
  components: { MockBanner },
  data() {
    return {
      liveOnly: true,
      groupBySite: false,
      rule: settings.valueRule,
      ruleOptions: [{ label: 'Average', value: 'average' }, { label: 'Highest', value: 'highest' }, { label: 'Lowest', value: 'lowest' }]
    }
  },
  computed: {
    rows() {
      return projects.flatMap(p => {
        const s = siteById(p.siteId)
        return productLines(p.id, this.rule)
          .filter(l => l.productType[0] !== '(')
          .filter(l => !this.liveOnly || l.countedValue !== null)
          .map(l => ({ ...l, projectId: p.id, projectName: p.name, siteName: s.name, town: s.town }))
      })
    },
    totalCounted() { return this.rows.reduce((a, r) => a + (r.countedValue || 0), 0) },
    totalLiveSum() { return this.rows.reduce((a, r) => a + r.liveSum, 0) },
    pct() { return this.totalLiveSum ? Math.round(100 * (this.totalLiveSum - this.totalCounted) / this.totalLiveSum) : 0 }
  },
  methods: { money }
}
</script>

<style scoped>
@import './sitesProjects.css';
</style>
